import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { videoService } from "../../services/videoService";
import { Video, Folder } from "../../types";
import { getImageUrl } from "../../services/api";
import {
  VideoCameraIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BookOpenIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";

const Videos: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch Root Videos (videos with no folder) and Root Folders
      // passing undefined for category, searchQuery, and true for root
      const [videoData, allFolders] = await Promise.all([
        videoService.getVideos(currentPage, 20, undefined, searchQuery, true),
        videoService.getFolders(),
      ]);

      setVideos(videoData.videos);
      setTotalPages(videoData.pagination?.totalPages || 1);

      // Filter for root folders (parent_id is null)
      // Assuming getFolders returns the tree where root items have parent_id === null
      const rootFolders = (allFolders || []).filter(
        (f) => f.parent_id === null,
      );

      // Sort folders by sort_order
      const sortedFolders = [...rootFolders].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );
      setFolders(sortedFolders);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Compact, responsive pagination controls
  const paginationControls = useMemo(() => {
    if (totalPages <= 1) return null;

    const getVisiblePages = () => {
      const delta = 2; // Show 2 pages on each side of current page
      const range: Array<number> = [];
      const rangeWithDots: Array<number | string> = [];

      for (
        let i = Math.max(2, currentPage - delta);
        i <= Math.min(totalPages - 1, currentPage + delta);
        i++
      ) {
        range.push(i);
      }

      if (currentPage - delta > 2) {
        rangeWithDots.push(1, "...");
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (currentPage + delta < totalPages - 1) {
        rangeWithDots.push("...", totalPages);
      } else if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }

      return rangeWithDots;
    };

    const visiblePages = getVisiblePages();

    return (
      <div className="flex justify-center items-center">
        <div className="flex items-center space-x-1 bg-white rounded-xl shadow-sm border border-gray-100 px-2 py-1">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-600 hover:text-indigo-600 hover:bg-indigo-50"
            }`}
          >
            <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="hidden sm:inline">ໜ້າກ່ອນ</span>
          </button>

          <div className="flex items-center space-x-1">
            {visiblePages.map((page, index) => (
              <button
                key={index}
                onClick={() =>
                  typeof page === "number" ? handlePageChange(page) : null
                }
                disabled={typeof page !== "number"}
                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  currentPage === page
                    ? "bg-indigo-600 text-white"
                    : typeof page === "number"
                      ? "text-gray-600 hover:text-indigo-600 hover:bg-indigo-50"
                      : "text-gray-400 cursor-default"
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-600 hover:text-indigo-600 hover:bg-indigo-50"
            }`}
          >
            <span className="hidden sm:inline">ໜ້າຕໍ່ໄປ</span>
            <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
          </button>
        </div>
      </div>
    );
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">ກຳລັງໂຫຼດ...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full mx-auto px-1 sm:px-2 py-4">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl mb-6 shadow-lg">
            <BookOpenIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-3">
            ຫໍສະໝຸດວີດີໂອ
          </h1>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto">
            ສຳຫຼວດຄໍເລັກຊັນວິດີໂອການສຶກສາຂອງພວກເຮົາ
          </p>
        </div>

        {/* Search Only */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ຄົ້ນຫາວິດີໂອ..."
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm text-lg"
                />
                <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              </div>
            </form>
          </div>
        </div>

        {/* Folders Section */}
        {folders.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FolderIcon className="h-7 w-7 text-indigo-500 mr-2" />
              ໂຟນເດີ
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {folders.map((folder) => (
                <Link
                  to={`/folder/${folder.id}`}
                  key={folder.id}
                  className="group relative bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 flex flex-col justify-center items-center h-20 sm:h-24 cursor-pointer hover:shadow-lg transform hover:-translate-y-1 hover:scale-105"
                >
                  <div className="w-full h-full flex items-center justify-center p-2 sm:p-3 relative bg-gradient-to-r from-purple-500 to-indigo-600">
                    <h3
                      className="text-sm sm:text-base font-bold text-white text-center truncate w-full px-2"
                      title={folder.name}
                    >
                      {folder.name}
                    </h3>

                    <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white bg-opacity-90 rounded-full p-1">
                        <BookOpenIcon className="h-4 w-4 text-indigo-600" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Videos Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {videos.map((video) => (
            <div
              key={video.id}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video bg-gray-100 overflow-hidden">
                {video.thumbnail_url && video.thumbnail_url.trim() !== "" ? (
                  <img
                    src={getImageUrl(video.thumbnail_url)}
                    alt={video.title}
                    className="h-full w-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{
                      backgroundColor: video.thumbnail_color || "#3B82F6",
                    }}
                    title={`Color: ${video.thumbnail_color || "#3B82F6"}`}
                  >
                    <PlayIcon className="h-12 w-12 text-white" />
                  </div>
                )}

                {/* Status Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {video.watched && (
                    <div className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md">
                      ເບີ່ງແລ້ວ
                    </div>
                  )}
                  {video.completed && (
                    <div className="bg-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md">
                      ສຳເລັດ
                    </div>
                  )}
                </div>

                {/* Duration Badge */}
                <div className="absolute bottom-3 right-3 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                  {videoService.formatDuration(video.duration)}
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                    <Link
                      to={`/videos/${video.id}`}
                      className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all duration-300 shadow-lg"
                    >
                      <PlayIcon className="h-4 w-4 mr-2" />
                      ເບີ່ງດຽວນີ້
                    </Link>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 text-left">
                <h3 className="text-xl font-bold text-indigo-600 mb-2 line-clamp-2 leading-tight">
                  {video.title}
                </h3>
                {video.description && (
                  <p className="text-sm sm:text-base text-gray-500 mb-3 line-clamp-2">
                    {video.description}
                  </p>
                )}
                <div className="flex items-center justify-start">
                  <span className="text-sm text-gray-400 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {videoService.formatDuration(video.duration)}
                  </span>
                </div>
                {video.sort_order !== undefined && video.sort_order > 0 && (
                  <div className="mt-2 text-xs text-gray-400">
                    Rank: {video.sort_order}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {paginationControls}

        {/* Empty State */}
        {videos.length === 0 && folders.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <VideoCameraIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              ບໍ່ມີວິດີໂອ
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              ບໍ່ມີວິດີໂອທີ່ພົບໃນການຄົ້ນຫາຂອງທ່ານ
            </p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors shadow-lg"
            >
              ລົບການກັ່ນຕອງ
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Videos;
